/**
 * Serviço de analytics com PostHog.
 *
 * Para ativar: setar VITE_ENABLE_ANALYTICS=true e VITE_POSTHOG_KEY no .env
 * PostHog inicializa automaticamente quando a key está presente.
 *
 * Eventos rastreados:
 *   - signup, login, onboarding_complete
 *   - first_transaction, transaction_created
 *   - plan_upgrade, plan_downgrade
 *   - feature_used, page_view
 */

import posthog from 'posthog-js';

const isEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
const posthogKey = import.meta.env.VITE_POSTHOG_KEY || '';
const isDev = import.meta.env.DEV;

let initialized = false;

if (isEnabled && posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: false,
    persistence: 'localStorage',
    autocapture: false,
  });
  initialized = true;
}

const analytics = {
  identify(userId, traits = {}) {
    if (!isEnabled) return;
    if (isDev) console.log('[analytics] identify', userId, traits);
    if (initialized) posthog.identify(userId, traits);
  },

  track(event, properties = {}) {
    if (!isEnabled) return;
    if (isDev) console.log('[analytics] track', event, properties);
    if (initialized) posthog.capture(event, properties);
  },

  page(path, properties = {}) {
    if (!isEnabled) return;
    if (isDev) console.log('[analytics] page', path);
    if (initialized) posthog.capture('$pageview', { $current_url: path, ...properties });
  },

  reset() {
    if (!isEnabled) return;
    if (isDev) console.log('[analytics] reset');
    if (initialized) posthog.reset();
  },

  // ── Eventos de negócio ──

  trackSignup(email, plan = 'free') {
    this.track('signup', { email, plan });
  },

  trackLogin(userId, method = 'email') {
    this.track('login', { userId, method });
  },

  trackOnboardingComplete(userId) {
    this.track('onboarding_complete', { userId });
  },

  trackTransactionCreated(type, amount, isFirst = false) {
    this.track('transaction_created', { type, amount });
    if (isFirst) this.track('first_transaction', { type, amount });
  },

  trackPlanUpgrade(fromPlan, toPlan, interval) {
    this.track('plan_upgrade', { from: fromPlan, to: toPlan, interval });
  },

  trackPlanDowngrade(fromPlan) {
    this.track('plan_downgrade', { from: fromPlan, to: 'free' });
  },

  trackFeatureUsed(feature) {
    this.track('feature_used', { feature });
  },
};

export default analytics;
